import React, { useState, useEffect, useRef } from 'react';
import { 
  Bell, 
  BellRing, 
  BellOff, 
  Check, 
  Sparkles, 
  Send, 
  X, 
  ExternalLink, 
  ShieldCheck, 
  Mail, 
  BookOpen, 
  Loader2, 
  Settings, 
  CheckCheck, 
  Trash2, 
  Folder, 
  Compass, 
  ChevronRight 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useWebPush } from '../hooks/useWebPush';
import { useAuth } from '../context/AuthContext';
import { useArticles } from '../context/ArticleContext';
import {
  NotificationHistoryItem,
  getNotificationHistory,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotificationFromHistory,
  clearNotificationHistory,
  syncArticlesToNotificationHistory
} from '../services/notificationHistoryService';

interface WebPushBellProps {
  className?: string;
}

export const WebPushBell: React.FC<WebPushBellProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const {
    isSupported,
    isSubscribed,
    isLoading,
    actionLoading,
    error,
    toastMessage,
    clearToast,
    clearError,
    subscribe,
    unsubscribe,
    testPush,
  } = useWebPush();

  const { currentUser, isMember, subscribeAndSignInWithEmail } = useAuth();
  const { articles } = useArticles();

  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'feed' | 'settings'>('feed');
  const [notifications, setNotifications] = useState<NotificationHistoryItem[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterSuccess, setNewsletterSuccess] = useState(false);
  
  const popoverRef = useRef<HTMLDivElement>(null);

  // Carrega e sincroniza o histórico de notificações
  const reloadNotifications = () => {
    const list = getNotificationHistory();
    setNotifications(list);
  };

  useEffect(() => {
    reloadNotifications();
    
    // Sincroniza artigos publicados recentemente
    if (articles && articles.length > 0) {
      syncArticlesToNotificationHistory(articles);
      reloadNotifications();
    }

    const handleUpdate = () => {
      reloadNotifications();
    };

    window.addEventListener('literafan:notification_history_updated', handleUpdate);
    return () => {
      window.removeEventListener('literafan:notification_history_updated', handleUpdate);
    };
  }, [articles]);

  // Fecha o popover ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const unreadCount = notifications.filter(n => !n.read).length;
  const isSubscriberOrMember = Boolean(currentUser?.isNewsletterSubscriber || isMember || isSubscribed);

  if (!isSupported) {
    return null;
  }

  const handleTogglePush = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      const emailToUse = emailInput.trim() || currentUser?.email || undefined;
      await subscribe(['articles', 'chapters', 'all'], emailToUse);
    }
  };

  const handleNewsletterSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailInput.trim()) return;

    setNewsletterLoading(true);
    try {
      await subscribeAndSignInWithEmail(emailInput.trim());
      await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailInput.trim(), source: 'bell_popover' })
      }).catch(() => {});

      if (!isSubscribed) {
        await subscribe(['articles', 'chapters', 'all'], emailInput.trim()).catch(() => {});
      }

      setNewsletterSuccess(true);
      setTimeout(() => setNewsletterSuccess(false), 5000);
    } catch (err) {
      console.warn('Erro na newsletter do sino:', err);
    } finally {
      setNewsletterLoading(false);
    }
  };

  const handleNotificationClick = (item: NotificationHistoryItem) => {
    markNotificationAsRead(item.id);
    reloadNotifications();
    setIsOpen(false);

    if (item.url) {
      if (item.url.startsWith('/#')) {
        navigate(item.url.replace('/#', ''));
      } else if (item.url.startsWith('#')) {
        navigate(item.url.replace('#', ''));
      } else {
        navigate(item.url);
      }
    }
  };

  const handleMarkAllRead = () => {
    markAllNotificationsAsRead();
    reloadNotifications();
  };

  const handleClearAll = () => {
    clearNotificationHistory();
    reloadNotifications();
  };

  const handleDeleteItem = (e: React.MouseEvent, id: string) => {
    e.stopPropagation();
    deleteNotificationFromHistory(id);
    reloadNotifications();
  };

  return (
    <div ref={popoverRef} className={`relative inline-block ${className} font-sans`}>
      {/* Botão do Sino com Badge Contador de Não Lidas */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Central de notificações e histórico de publicações"
        title={unreadCount > 0 ? `${unreadCount} novas notificações` : "Central de Notificações Literafan"}
        className={`relative p-2 sm:p-2.5 rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer ${
          isSubscribed || unreadCount > 0
            ? 'text-primary bg-primary/15 hover:bg-primary/25 border border-primary/40 shadow-[0_0_15px_rgba(193,18,31,0.3)]'
            : 'text-stone-400 hover:text-stone-200 hover:bg-stone-800/60 border border-transparent'
        }`}
      >
        {isSubscribed ? (
          <BellRing className="w-5 h-5 text-primary animate-pulse" />
        ) : (
          <Bell className="w-5 h-5" />
        )}
        
        {/* Badge contador */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-gradient-to-tr from-primary to-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-stone-950 shadow-lg animate-scale-in">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Popover / Menu Dropdown da Central de Notificações */}
      {isOpen && (
        <div className="fixed sm:absolute right-3 sm:right-0 top-16 sm:top-auto sm:mt-3 w-[calc(100vw-24px)] sm:w-[380px] md:w-[420px] max-h-[85vh] flex flex-col rounded-2xl sm:rounded-3xl bg-stone-950/95 backdrop-blur-2xl border border-primary/40 shadow-[0_20px_50px_rgba(0,0,0,0.9),0_0_25px_rgba(193,18,31,0.25)] z-50 animate-in fade-in zoom-in-95 duration-200 overflow-hidden">
          {/* Top illuminated line */}
          <div className="h-1 bg-gradient-to-r from-primary via-red-500 to-rose-600 shrink-0" />

          {/* Header */}
          <div className="p-3.5 sm:p-4 pb-3 border-b border-stone-800/80 bg-stone-900/60 shrink-0 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-xl bg-primary/15 border border-primary/30 text-primary">
                <BellRing className="w-4 h-4" />
              </div>
              <div>
                <h3 className="text-sm font-serif font-bold text-stone-100 flex items-center gap-2 leading-none">
                  Central de Notificações
                  {unreadCount > 0 && (
                    <span className="px-1.5 py-0.5 rounded-full bg-primary/20 border border-primary/40 text-primary text-[10px] font-sans font-bold">
                      {unreadCount} nova{unreadCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </h3>
                <p className="text-[11px] text-stone-400 mt-0.5">
                  {isSubscriberOrMember ? 'Alertas & Lançamentos em primeira mão' : 'Inscreva-se para receber novidades'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={() => setActiveTab(activeTab === 'feed' ? 'settings' : 'feed')}
                className={`p-1.5 rounded-xl border text-xs transition-colors ${
                  activeTab === 'settings'
                    ? 'bg-primary text-white border-primary'
                    : 'bg-stone-900 hover:bg-stone-800 text-stone-400 hover:text-stone-200 border-stone-800'
                }`}
                title={activeTab === 'settings' ? 'Ver Histórico de Avisos' : 'Configurações de Notificação'}
              >
                <Settings className="w-4 h-4" />
              </button>

              <button
                onClick={() => {
                  setIsOpen(false);
                  clearError();
                  clearToast();
                }}
                className="p-1.5 rounded-xl bg-stone-900 hover:bg-stone-800 text-stone-400 hover:text-stone-200 border border-stone-800 transition-colors"
                aria-label="Fechar"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Subheader / Tabs & Quick Actions */}
          <div className="px-3.5 sm:px-4 py-2 border-b border-stone-800/60 bg-stone-950/80 shrink-0 flex items-center justify-between text-xs">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setActiveTab('feed')}
                className={`px-2.5 py-1 rounded-lg font-medium transition-colors ${
                  activeTab === 'feed'
                    ? 'bg-stone-900 text-stone-100 border border-stone-700'
                    : 'text-stone-400 hover:text-stone-200'
                }`}
              >
                Histórico ({notifications.length})
              </button>
              <button
                onClick={() => setActiveTab('settings')}
                className={`px-2.5 py-1 rounded-lg font-medium transition-colors ${
                  activeTab === 'settings'
                    ? 'bg-stone-900 text-stone-100 border border-stone-700'
                    : 'text-stone-400 hover:text-stone-200'
                }`}
              >
                Ajustes & Push
              </button>
            </div>

            {activeTab === 'feed' && notifications.length > 0 && (
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-[11px] text-stone-400 hover:text-primary transition-colors flex items-center gap-1"
                    title="Marcar todas como lidas"
                  >
                    <CheckCheck size={12} />
                    <span>Lidas</span>
                  </button>
                )}
                <button
                  onClick={handleClearAll}
                  className="text-[11px] text-stone-500 hover:text-stone-300 transition-colors flex items-center gap-1"
                  title="Limpar histórico"
                >
                  <Trash2 size={12} />
                  <span>Limpar</span>
                </button>
              </div>
            )}
          </div>

          {/* Body Content with Scroll */}
          <div className="overflow-y-auto flex-1 max-h-[55vh] sm:max-h-[60vh] p-3 sm:p-4 space-y-2.5 hide-scrollbar">
            {/* Feedback Messages */}
            {toastMessage && (
              <div className="p-2.5 rounded-xl bg-emerald-950/60 border border-emerald-500/40 text-emerald-300 text-xs flex items-center justify-between animate-fade-in">
                <span className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5 shrink-0" />
                  {toastMessage}
                </span>
                <button onClick={clearToast} className="text-emerald-400 hover:text-emerald-200">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {error && (
              <div className="p-2.5 rounded-xl bg-rose-950/60 border border-rose-500/40 text-rose-300 text-xs flex items-center justify-between animate-fade-in">
                <span>{error}</span>
                <button onClick={clearError} className="text-rose-400 hover:text-rose-200">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* TAB 1: NOTIFICATION FEED / HISTÓRICO */}
            {activeTab === 'feed' && (
              <>
                {notifications.length === 0 ? (
                  <div className="py-12 px-4 text-center">
                    <div className="w-12 h-12 rounded-full bg-stone-900 border border-stone-800 flex items-center justify-center mx-auto text-stone-600 mb-3">
                      <Bell size={20} />
                    </div>
                    <p className="text-stone-300 text-sm font-medium font-serif mb-1">Nenhuma notificação recente</p>
                    <p className="text-stone-500 text-xs max-w-xs mx-auto">
                      Quando novos artigos, sagas ou capítulos forem lançados, seus avisos aparecerão registrados aqui.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {notifications.map((item) => (
                      <div
                        key={item.id}
                        onClick={() => handleNotificationClick(item)}
                        className={`group relative p-3 sm:p-3.5 rounded-2xl border transition-all duration-200 cursor-pointer flex gap-3 items-start ${
                          item.read
                            ? 'bg-stone-900/40 hover:bg-stone-900/80 border-stone-800/80 text-stone-400'
                            : 'bg-stone-900/90 hover:bg-stone-850 border-primary/40 shadow-sm text-stone-200 ring-1 ring-primary/20'
                        }`}
                      >
                        {/* Unread indicator */}
                        {!item.read && (
                          <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-primary ring-2 ring-stone-950 animate-pulse" />
                        )}

                        {/* Thumbnail / Category Icon */}
                        <div className="w-11 h-11 rounded-xl overflow-hidden bg-stone-950 border border-stone-800 shrink-0 flex items-center justify-center relative">
                          {item.imageUrl ? (
                            <img
                              src={item.imageUrl}
                              alt=""
                              aria-hidden="true"
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              referrerPolicy="no-referrer"
                            />
                          ) : item.category === 'chapter' ? (
                            <Folder className="w-5 h-5 text-primary" />
                          ) : item.category === 'welcome' ? (
                            <Sparkles className="w-5 h-5 text-primary" />
                          ) : (
                            <BookOpen className="w-5 h-5 text-stone-400" />
                          )}
                        </div>

                        {/* Text details */}
                        <div className="flex-1 min-w-0 pr-3">
                          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-primary/20 text-primary border border-primary/30 uppercase tracking-wider">
                              {item.categoryLabel}
                            </span>
                            <span className="text-[10px] text-stone-500 font-mono">
                              {item.date}
                            </span>
                          </div>

                          <h4 className={`text-xs sm:text-sm font-semibold leading-snug line-clamp-2 transition-colors ${
                            item.read ? 'text-stone-300 group-hover:text-primary' : 'text-stone-100 group-hover:text-primary font-serif'
                          }`}>
                            {item.title}
                          </h4>

                          <p className="text-[11px] text-stone-400 line-clamp-2 mt-1 leading-relaxed">
                            {item.body}
                          </p>
                        </div>

                        {/* Hover delete single */}
                        <button
                          onClick={(e) => handleDeleteItem(e, item.id)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-stone-500 hover:text-stone-300 transition-opacity absolute bottom-2 right-2 rounded hover:bg-stone-800"
                          title="Remover notificação"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* TAB 2: CONFIGURAÇÕES & DISPOSITIVO */}
            {activeTab === 'settings' && (
              <div className="space-y-4 py-1">
                {/* Status Box */}
                <div className="p-3.5 rounded-2xl bg-stone-900/80 border border-stone-800 flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className={`p-2 rounded-xl border ${
                      isSubscribed 
                        ? 'bg-emerald-950/60 border-emerald-500/40 text-emerald-400' 
                        : 'bg-stone-800 border-stone-700 text-stone-400'
                    }`}>
                      {isSubscribed ? <ShieldCheck size={18} /> : <BellOff size={18} />}
                    </div>
                    <div>
                      <p className="text-xs font-bold text-stone-200">
                        {isSubscribed ? 'Notificações Web Push Ativas' : 'Notificações Não Ativadas'}
                      </p>
                      <p className="text-[11px] text-stone-400">
                        {isSubscribed 
                          ? 'Seu navegador está apto a receber alertas em tempo real.' 
                          : 'Ative para receber avisos imediatos de novos capítulos.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Newsletter binding */}
                {!currentUser?.isNewsletterSubscriber && !isMember && (
                  <form onSubmit={handleNewsletterSubscribe} className="p-3.5 rounded-2xl bg-stone-900/60 border border-stone-800">
                    <label className="text-[11px] font-medium text-stone-300 block mb-1.5 flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-primary" />
                      <span>Conectar e-mail da Newsletter:</span>
                    </label>
                    <div className="flex gap-1.5">
                      <input
                        type="email"
                        value={emailInput}
                        onChange={(e) => setEmailInput(e.target.value)}
                        placeholder="Seu melhor e-mail"
                        className="flex-1 px-3 py-1.5 bg-stone-950 border border-stone-700/80 rounded-xl text-xs text-stone-200 placeholder-stone-500 focus:outline-none focus:border-primary"
                      />
                      <button
                        type="submit"
                        disabled={newsletterLoading || !emailInput.trim()}
                        className="px-3 py-1.5 bg-primary hover:bg-red-700 text-white text-xs font-semibold rounded-xl transition-all disabled:opacity-50 flex items-center gap-1 shrink-0"
                      >
                        {newsletterLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Inscrever'}
                      </button>
                    </div>
                    {newsletterSuccess && (
                      <p className="text-[11px] text-emerald-400 mt-1.5 flex items-center gap-1">
                        <Check className="w-3 h-3" /> Inscrito na newsletter com sucesso!
                      </p>
                    )}
                  </form>
                )}

                {/* Action Buttons */}
                <div className="space-y-2 pt-1">
                  {isSubscribed ? (
                    <>
                      <button
                        onClick={testPush}
                        disabled={actionLoading}
                        className="w-full py-2.5 px-3 rounded-xl bg-stone-900 hover:bg-stone-850 text-stone-200 text-xs font-semibold transition flex items-center justify-center gap-2 border border-stone-700/80 disabled:opacity-50"
                      >
                        <Send className="w-3.5 h-3.5 text-primary" />
                        <span>{actionLoading ? 'Disparando...' : 'Enviar notificação de teste'}</span>
                      </button>

                      <button
                        onClick={handleTogglePush}
                        disabled={actionLoading}
                        className="w-full py-2 px-3 rounded-xl bg-stone-950 hover:bg-rose-950/40 text-stone-400 hover:text-rose-400 text-xs font-medium transition flex items-center justify-center gap-2 border border-stone-800 hover:border-rose-800/40 disabled:opacity-50"
                      >
                        <BellOff className="w-3.5 h-3.5" />
                        <span>Desativar neste dispositivo</span>
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleTogglePush}
                      disabled={actionLoading}
                      className="w-full py-2.5 px-4 rounded-xl bg-gradient-to-r from-primary via-red-600 to-rose-700 hover:from-red-600 hover:to-primary text-white font-bold text-xs tracking-wide transition shadow-lg shadow-primary/25 flex items-center justify-center gap-2 disabled:opacity-50 active:scale-95"
                    >
                      <Bell className="w-4 h-4" />
                      <span>{actionLoading ? 'Solicitando permissão...' : 'Ativar Notificações Web Push'}</span>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer banner */}
          <div className="p-3 border-t border-stone-800/80 bg-stone-950/90 shrink-0 text-center flex items-center justify-between text-[11px] text-stone-400">
            <span className="flex items-center gap-1.5">
              <Sparkles size={12} className="text-primary" />
              <span>Literafan • Clube Literário</span>
            </span>
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/artigos');
              }}
              className="text-stone-300 hover:text-primary transition-colors flex items-center gap-0.5"
            >
              <span>Ver Catálogo</span>
              <ChevronRight size={12} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WebPushBell;
